<template>
  <div>
    <v-layout row wrap align-center class="mb-2" style="gap: 12px">
      <v-flex xs12 sm4>
        <v-select
          v-model="topicLevel1Filter"
          label="Filter by topic level 1"
          :items="topicLevel1Options"
          clearable
          hide-details
        />
      </v-flex>
    </v-layout>
    <base-card title="Topic distribution by MAD sessions">
      <template slot="content">
        <div v-if="hasData" style="height: 400px; position: relative">
          <topic-distribution-chart :chart-data="chartData" :chart-options="chartOptions" />
        </div>
        <div v-else class="text-xs-center pa-4 grey--text">
          No data available
        </div>
        <v-layout align-center justify-end class="mt-2" style="gap: 8px">
          <v-btn icon small :disabled="showAll || page <= 1" @click="changePage(page - 1)">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <span class="grey--text">{{ showAll ? `All (${totalTopics})` : `${page} / ${totalPages}` }}</span>
          <v-btn icon small :disabled="showAll || page >= totalPages" @click="changePage(page + 1)">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
          <v-btn small :outline="!showAll" color="primary" @click="toggleShowAll">All</v-btn>
        </v-layout>
      </template>
    </base-card>
  </div>
</template>

<script>
import { HorizontalBar } from 'vue-chartjs';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import BaseCard from 'components/BaseCard';
import { mapState, mapActions } from 'vuex';
import ReportService from '@/services/report.service';

const CHART_COLORS = [
  '#3d5b96', '#e67e22', '#27ae60', '#8e44ad', '#c0392b',
  '#16a085', '#d35400', '#2980b9', '#f39c12', '#1abc9c',
  '#e74c3c', '#9b59b6', '#2ecc71', '#3498db', '#e67e22'
];

const TopicDistributionChart = {
  extends: HorizontalBar,
  props: ['chartData', 'chartOptions'],
  watch: {
    chartData() {
      this.$data._chart.destroy();
      this.renderChart(this.chartData, this.chartOptions);
    }
  },
  mounted() {
    this.addPlugin(ChartDataLabels);
    this.renderChart(this.chartData, this.chartOptions);
  }
};

export default {
  name: 'OverviewReportLiveSupport',
  components: { BaseCard, TopicDistributionChart },
  data() {
    return {
      topicLevel1Filter: '',
      topicLevel1Options: [],
      page: 1,
      pageSize: 10,
      showAll: false
    };
  },
  computed: {
    ...mapState('overviewReport', ['topicDistribution', 'filter']),
    rows() {
      return (this.topicDistribution && this.topicDistribution.items) || [];
    },
    totalTopics() {
      return (this.topicDistribution && this.topicDistribution.total_topics) || 0;
    },
    totalPages() {
      return (this.topicDistribution && this.topicDistribution.total_pages) || 1;
    },
    hasData() {
      return this.rows.length > 0;
    },
    allLevel2Names() {
      if (!this.hasData) return [];
      const names = new Set();
      this.rows.forEach(row => {
        (row.level_2 || []).forEach(l2 => {
          names.add(l2.name || '');
        });
      });
      return [...names].sort((a, b) => (a || '').localeCompare(b || ''));
    },
    chartData() {
      if (!this.hasData) return { labels: [], datasets: [] };
      const self = this;
      const labels = this.rows.map(row => row.level_1);
      const datasets = this.allLevel2Names.map((l2Name, idx) => ({
        label: l2Name || '(Unclassified)',
        backgroundColor: CHART_COLORS[idx % CHART_COLORS.length],
        data: this.rows.map(row => {
          const found = (row.level_2 || []).find(l => (l.name || '') === l2Name);
          return found ? found.count : 0;
        })
      }));
      // Show the row total once, anchored past the end of the whole stacked bar —
      // only the last dataset in stacking order sits at the final cumulative position.
      const lastDataset = datasets[datasets.length - 1];
      if (lastDataset) {
        lastDataset.datalabels = {
          display: true,
          anchor: 'end',
          align: 'right',
          color: '#333',
          font: { weight: 'bold' },
          formatter: (value, ctx) => self.rows[ctx.dataIndex].total
        };
      }
      return { labels, datasets };
    },
    chartOptions() {
      const self = this;
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          xAxes: [{ stacked: true, ticks: { beginAtZero: true } }],
          yAxes: [{ stacked: true, categoryPercentage: 0.6, barPercentage: 1 }]
        },
        plugins: {
          datalabels: { display: false }
        },
        tooltips: {
          callbacks: {
            title(tooltipItems) {
              const idx = tooltipItems[0].index;
              const row = self.rows[idx];
              return `${row.level_1} (Total MAD sessions: ${row.total})`;
            },
            label(tooltipItem) {
              const l2Name = self.allLevel2Names[tooltipItem.datasetIndex];
              const displayName = l2Name || '(Unclassified)';
              const idx = tooltipItem.index;
              const row = self.rows[idx];
              const found = (row.level_2 || []).find(l => (l.name || '') === l2Name);
              if (!found) return null;
              return `${displayName}: ${found.count} (${found.percentage}%)`;
            },
            filter(item) {
              return item.xLabel > 0;
            }
          }
        }
      };
    }
  },
  watch: {
    topicLevel1Filter() {
      this.page = 1;
      this.reload();
    },
    'filter.from'() {
      this.page = 1;
      this.reload();
    },
    'filter.to'() {
      this.page = 1;
      this.reload();
    },
    'filter.channel'() {
      this.page = 1;
      this.reload();
    }
  },
  mounted() {
    this.loadTopicOptions();
    this.reload();
  },
  methods: {
    ...mapActions('overviewReport', ['fetchTopicDistribution']),
    reload() {
      this.fetchTopicDistribution({
        topicLevel1Filter: this.topicLevel1Filter,
        page: this.page,
        pageSize: this.pageSize,
        all: this.showAll
      });
    },
    changePage(newPage) {
      if (newPage < 1 || newPage > this.totalPages) return;
      this.page = newPage;
      this.reload();
    },
    toggleShowAll() {
      this.showAll = !this.showAll;
      this.page = 1;
      this.reload();
    },
    loadTopicOptions() {
      const botCode = this.$route.params.bot_code;
      ReportService.getTopicOptions(botCode)
        .then(res => {
          const topics = Array.isArray(res.data) ? res.data : [];
          this.topicLevel1Options = topics.map(t => t.name);
        })
        .catch(() => { this.topicLevel1Options = []; });
    }
  }
};
</script>
